const smoothingFactor = (cutoffHz: number, deltaSeconds: number): number => {
  const tau = 1 / (2 * Math.PI * cutoffHz);
  return 1 / (1 + tau / deltaSeconds);
};

class LowPass {
  #value?: number;

  update(value: number, alpha: number): number {
    this.#value = this.#value === undefined ? value : alpha * value + (1 - alpha) * this.#value;
    return this.#value;
  }

  reset(): void {
    this.#value = undefined;
  }
}

/** Causal one-euro filter: it buffers no frame and emits on the same call. */
export class OneEuroFilter {
  readonly #signal = new LowPass();
  readonly #derivative = new LowPass();
  #lastRaw?: number;
  #lastSeconds?: number;

  constructor(
    readonly minCutoffHz = 1.4,
    readonly beta = 0.08,
    readonly derivativeCutoffHz = 1,
  ) {}

  update(value: number, timestampSeconds: number): number {
    if (this.#lastSeconds === undefined || timestampSeconds <= this.#lastSeconds) {
      this.#lastSeconds = timestampSeconds;
      this.#lastRaw = value;
      return this.#signal.update(value, 1);
    }
    const delta = Math.min(0.25, Math.max(1 / 240, timestampSeconds - this.#lastSeconds));
    const rawDerivative = (value - this.#lastRaw!) / delta;
    const derivative = this.#derivative.update(rawDerivative, smoothingFactor(this.derivativeCutoffHz, delta));
    const cutoff = this.minCutoffHz + this.beta * Math.abs(derivative);
    this.#lastSeconds = timestampSeconds;
    this.#lastRaw = value;
    return this.#signal.update(value, smoothingFactor(cutoff, delta));
  }

  reset(): void {
    this.#signal.reset();
    this.#derivative.reset();
    this.#lastRaw = undefined;
    this.#lastSeconds = undefined;
  }
}
